import {
  PayType,
  PayTypeMap,
  ProductStock,
  ProductStockStatus,
  addProductStock,
  updateProductInfo,
  updateProductShipping,
  updateProductStock,
} from '@/api/product';
import { uploadResourcePositionImage } from '@/api/upload';
import { commonOptionsAtom } from '@/atoms/commonOptions';
import { productCategoriesAtom } from '@/atoms/productCategories';
import RichEditor, { RichEditorRef } from '@/components/RichEditor';
import ImagesUploader from '@/components/ImagesUploader';
import UploadImage from '@/components/UploadImage';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Divider,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Upload,
  message,
} from 'antd';
import { useAtom } from 'jotai';
import React, { memo, useEffect, useRef, useState } from 'react';
import TimeLimit from './Timelimit';
import FormRichEditor from '@/components/FormRichEditor';

interface ExtraContent {
  type: 'image' | 'text';
  imageInfo?: {
    uid: string;
    name: string;
    url: string;
    originFileObj?: File;
  };
  text?: string;
  editorRef?: React.RefObject<RichEditorRef>;
}

interface ProductDrawerProps {
  onSuccess: () => void;
  onClose: () => void;
  open: boolean;
  editingProduct?: ProductStock | null;
}

const ProductDrawer: React.FC<ProductDrawerProps> = ({
  onSuccess,
  onClose,
  open,
  editingProduct,
}) => {
  const [uploadLoading, setUploadLoading] = useState(false);
  const [commonOptions] = useAtom(commonOptionsAtom);
  const [categories] = useAtom(productCategoriesAtom);
  const editorRefsMap = useRef<Map<number, React.RefObject<RichEditorRef>>>(
    new Map()
  );

  // 获取编辑器内容
  const getEditorContent = (index: number) => {
    const ref = editorRefsMap.current.get(index);
    return [ref?.current?.getHtml?.() || '', ref?.current?.getDelta?.() || ''];
  };

  const [form] = Form.useForm();

  // 初始化表单数据
  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    if (editingProduct) {
      // 编辑模式：填充现有数据
      const extContent = editingProduct.productExtData
        ? JSON.parse(editingProduct.productExtData)
        : [];

      console.log('原始 extContent:', extContent);

      const formattedExtContent = extContent.map((item: any) => ({
        type: item.type,
        ...(item.type === 'image'
          ? {
              imageInfo: {
                uid: item.imageUrl,
                name: 'image',
                url: item.imageUrl,
              },
              // 保存 imageUrl 和 ratio，用于后续编辑时保留
              imageUrl: item.imageUrl,
              ratio: item.ratio || 1,
            }
          : {
              text: item.deltaContent,
            }),
      }));


      // 处理图片数据
      const formatImages = (imagesStr: string | null) => {
        if (!imagesStr) return [];
        try {
          const images = JSON.parse(imagesStr);
          const formattedImages = Array.isArray(images)
            ? images.map((img) => ({
                imageUrl: img.imageUrl,
                ratio: img.ratio || 1, // 如果没有 ratio，默认为 1
              }))
            : [];
          return formattedImages;
        } catch (e) {
          console.error('解析图片数据失败:', e);
          return [];
        }
      };

      // 先设置其他字段
      const formData = {
        productStockStatus: editingProduct.productStockStatus,
        payType: editingProduct.payType,
        productTitle: editingProduct.productTitle,
        // 确保 productCategoryId 是数字类型
        productCategoryId: editingProduct.productCategoryId,
        productShowImages: formatImages(editingProduct.productShowImages),
        stock: editingProduct.stock,
        stockCode: editingProduct.stockCode,
        price: editingProduct.price,
        num: editingProduct.num,
        prePersonLimit: editingProduct.prePersonLimit,
        shippingInformation: editingProduct.shippingInformation,
        disclaimer: editingProduct.disclaimer,
        productDesc: editingProduct.productDesc,
        extContent: formattedExtContent,
        avatarFrameImage: (editingProduct as any).avatarFrameImage || '',
        backgroundImage: (editingProduct as any).backgroundImage || '',
        // productionRequiremenImage: [{ imageUrl: editingProduct.productionRequiremenImage }]
      };

      if (editingProduct.productCategoryId == 5) {
        const productionRequiremenDesc = editingProduct.productionRequiremenDesc
          ? [JSON.parse(editingProduct.productionRequiremenDesc)]
          : [];
        const formattedProductionRequiremenDesc = productionRequiremenDesc.map(
          (item: any) => ({
            type: item.type,
            ...(item.type === 'image'
              ? {
                  imageInfo: {
                    uid: item.imageUrl,
                    name: 'image',
                    url: item.imageUrl,
                  },
                }
              : {
                  text: item.deltaContent,
                }),
          })
        );

        formData.productionRequiremenDesc =
          formattedProductionRequiremenDesc[0];
      }

      setTimeout(() => {
        form.setFieldsValue(formData);

        // 等待分类ID设置完成后，再设置壁纸文件
        setTimeout(() => {
          if (
            editingProduct.productCategoryId === 1 ||
            editingProduct.productCategoryId === '1'
          ) {
            console.log('设置壁纸文件值');
            form.setFieldValue(
              'productImages',
              formatImages(editingProduct.productImages)
            );
          }

          if (editingProduct.productCategoryId == 5) {
            const productionRequiremenImage = JSON.stringify([
              { imageUrl: editingProduct.productionRequiremenImage },
            ]);
            // form.setFieldValue("productionRequiremenImage", formatImages(productionRequiremenImage));

            let orderValidityPeriod = editingProduct.orderValidityPeriod;
            if (editingProduct.orderValidityPeriodFlag === 1) {
              orderValidityPeriod = -1;
            } else {
              orderValidityPeriod = editingProduct.orderValidityPeriod;
            }

            form.setFieldsValue({
              productionRequiremenImage: formatImages(
                productionRequiremenImage
              ),
              orderValidityPeriod,
              productionRequiremenDesc: formData.productionRequiremenDesc,
            });
          }
        });
      }, 0);
    } else {
      // 新增模式：设置默认值
      form.setFieldsValue({
        productStockStatus: ProductStockStatus.OffShelf,
        payType: PayType.Points,
        stock: 0,
        price: 0,
        productShowImages: [],
        extContent: [],
      });
    }
    editorRefsMap.current.clear();

    // 清理函数
    return () => {
      form.resetFields();
      editorRefsMap.current.clear();
    };
  }, [open, editingProduct]);

  // 处理添加扩展内容
  const handleAddExtraContent = (type: 'image' | 'text') => {
    const currentContent = form.getFieldValue('extContent') || [];
    const newContent = [...currentContent];
    newContent.push({
      type,
    });
    if (type === 'text') {
      const index = newContent.length - 1;
      editorRefsMap.current.set(index, React.createRef());
    }
    form.setFieldValue('extContent', newContent);
  };

  // 处理删除扩展内容
  const handleRemoveExtraContent = (index: number) => {
    const currentContent = form.getFieldValue('extContent') || [];
    const newContent = [...currentContent];
    newContent.splice(index, 1);
    editorRefsMap.current.delete(index);
    // 重新映射索引
    newContent.forEach((_, i) => {
      if (i >= index && editorRefsMap.current.has(i + 1)) {
        const ref = editorRefsMap.current.get(i + 1);
        editorRefsMap.current.set(i, ref);
        editorRefsMap.current.delete(i + 1);
      }
    });
    form.setFieldValue('extContent', newContent);
  };

  // 处理图片上传
  const handleExtraImageChange = (info: any, index: number) => {
    const currentContent = form.getFieldValue('extContent') || [];
    const newContent = [...currentContent];
    
    // 保留原有的字段（type, imageUrl, ratio等）
    const existingItem = newContent[index] || {};
    
    if (info.fileList.length > 0) {
      const file = info.fileList[0];
      newContent[index] = {
        ...existingItem,
        type: existingItem.type || 'image', // 确保保留 type
        imageInfo: {
          uid: file.uid,
          name: file.name,
          url: URL.createObjectURL(file.originFileObj),
          originFileObj: file.originFileObj, // 保存原始文件对象用于上传
        },
        // 保留原有的 imageUrl 和 ratio（如果有）
        imageUrl: existingItem.imageUrl,
        ratio: existingItem.ratio,
      };
    } else {
      newContent[index] = {
        ...existingItem,
        type: existingItem.type || 'image', // 确保保留 type
        imageInfo: undefined,
        // 保留原有的 imageUrl 和 ratio（如果有）
        imageUrl: existingItem.imageUrl,
        ratio: existingItem.ratio,
      };
    }
    form.setFieldValue('extContent', newContent);
  };

  const onSubmit = async () => {
    try {
      // 在验证前，确保分类ID为6时头像框字段有默认值
      const currentCategoryId = form.getFieldValue('productCategoryId');
      if (currentCategoryId === 6 || currentCategoryId === '6' || Number(currentCategoryId) === 6) {
        const currentAvatarFrameImage = form.getFieldValue('avatarFrameImage');
        const currentAvatarFrameBgImage = form.getFieldValue('backgroundImage');
        // 如果字段值为 undefined，设置为空字符串
        if (currentAvatarFrameImage === undefined || currentAvatarFrameImage === null) {
          form.setFieldValue('avatarFrameImage', '');
        }
        if (currentAvatarFrameBgImage === undefined || currentAvatarFrameBgImage === null) {
          form.setFieldValue('backgroundImage', '');
        }
      }
      
      const values = await form.validateFields();
      setUploadLoading(true);
      // 从表单中获取扩展内容
      const extContent = values.extContent || [];
      const productExtData = await Promise.all(
        extContent.map(async (item: ExtraContent, index: number) => {
          if (item.type === 'image') {
            // 如果没有新上传的文件，使用已有的 imageUrl 和 ratio
            if (!item.imageInfo?.originFileObj) {
              // 优先使用已有的 imageUrl 和 ratio（编辑模式下保留的数据）
              if ((item as any).imageUrl) {
                return {
                  type: 'image',
                  imageUrl: (item as any).imageUrl,
                  ratio: (item as any).ratio || 1,
                  content: (item as any).imageUrl,
                };
              }
              // 否则使用 imageInfo 中的 url
              return {
                type: 'image',
                content: item.imageInfo?.url || '',
              };
            }
            // 有新上传的文件，需要上传并计算比例
            const file = item.imageInfo.originFileObj;
            const img = new Image();
            img.src = URL.createObjectURL(file);
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });
            const ratio = Number((img.width / img.height).toFixed(2));
            const resp = await uploadResourcePositionImage(file);
            return {
              type: 'image',
              imageUrl: resp.data,
              ratio,
              content: resp.data,
            };
          } else {
            const [htmlContent, deltaContent] = getEditorContent(index);
            return {
              type: 'text',
              htmlContent,
              deltaContent: JSON.stringify(deltaContent),
            };
          }
        })
      );

      const params = {
        ...values,
        productImages: values.productImages || [],
        productShowImages: values.productShowImages || [],
        productExtData: JSON.stringify(productExtData),
      };

      if (values.productCategoryId == 5) {
        if (values.orderValidityPeriod === -1) {
          params.orderValidityPeriodFlag = 1;
          delete params.orderValidityPeriod;
        } else {
          params.orderValidityPeriodFlag = 0;
        }

        if (values.productionRequiremenImage?.length) {
          params.productionRequiremenImage =
            values.productionRequiremenImage[0].imageUrl;
        }
      }

      if (editingProduct) {
        await updateProductInfo({
          ...params,
          productStockId: editingProduct.productStockId,
        });
        await updateProductShipping({
          ...params,
          productStockId: editingProduct.productStockId,
        });
        await updateProductStock({
          ...params,
          productStockId: editingProduct.productStockId,
        });
        message.success('商品更新成功');
      } else {
        const res = await addProductStock(params);
        if (res.status === 200) {
          message.success('商品添加成功');
        } else {
          message.success('商品添加失败');
        }
      }
      onSuccess();
    } catch (error) {
      console.error(error);
      message.error('操作失败，请重试');
      return false;
    } finally {
      setUploadLoading(false);
      return false;
    }
  };

  const onCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title={editingProduct ? '编辑商品' : '新增商品'}
      width={800}
      onClose={onCancel}
      open={open}
      placement={'right'}
      styles={{
        body: { paddingBottom: 80 },
      }}
      destroyOnClose={false}
      maskClosable={false}
      extra={
        <Space>
          <Button onClick={onCancel}>取消</Button>
          <Button
            type="primary"
            onClick={onSubmit}
            // loading={submitting || loading}
          >
            确定
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Spin spinning={uploadLoading} tip="加载中...">
          {/* 基本信息部分 */}
          <Form.Item
            name="productShowImages"
            label="展示图"
            rules={[
              {
                required: true,
                message: '请上传商品展示图',
                validator: (_, value) => {
                  console.log('展示图验证值:', value);
                  if (!value || value.length === 0) {
                    return Promise.reject('请上传商品展示图');
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra="图片要求：比例为1:1，支持格式：jpg、jpeg、png、webp、gif、svga、lottie（.lottie/.json），10MB以内"
          >
            <ImagesUploader 
              limit={18}
              fileTypes={[
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/webp',
                'image/gif',
                '.svga'
              ]}
              allowLottieSvga={true}
              showDelete={true}
            />
          </Form.Item>

          <Form.Item
            name="productTitle"
            label="商品标题"
            rules={[{ required: true, message: '请输入商品标题' }]}
          >
            <Input placeholder="请输入商品标题" maxLength={50} showCount />
          </Form.Item>

          <Form.Item
            name="productCategoryId"
            label="商品分类"
            rules={[{ required: true, message: '请选择商品分类' }]}
          >
            <Select
              placeholder="请选择商品分类"
              disabled={categories.length === 0}
            >
              {categories.map((category) => (
                <Select.Option
                  key={category.productCategoryId}
                  value={category.productCategoryId}
                >
                  {category.categoryTitle}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item noStyle dependencies={['productCategoryId']}>
            {({ getFieldValue }) => {
              const categoryId = getFieldValue('productCategoryId');
              return (
                <>
                  {(categoryId === 6 || categoryId === '6') && (
                    <Form.Item
                      name="avatarFrameImage"
                      label="头像框"
                      preserve
                      rules={[
                        {
                          required: true,
                          message: '请上传商品头像框',
                        },
                      ]}
                      extra="请上传商品头像框，支持：jpg、jpeg、png、webp、gif、svga、lottie（.lottie/.json）"
                    >
                      <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
                        prevValues.avatarFrameImage !== currentValues.avatarFrameImage
                      }>
                        {() => {
                          const avatarValue = form.getFieldValue('avatarFrameImage');
                          // 如果字段值为空，尝试从编辑产品数据中获取
                          const finalAvatarValue = avatarValue || (editingProduct as any)?.avatarFrameImage || '';

                          return (
                            <UploadImage 
                              value={finalAvatarValue}
                              onChange={(url) => form.setFieldValue('avatarFrameImage', url)}
                              fileTypes={[
                                'image/jpeg',
                                'image/jpg',
                                'image/png',
                                'image/webp',
                                'image/gif',
                                '.svga'
                              ]}
                              allowLottieSvga
                              width={104}
                              height={104}
                              showSizeHint={false}
                            />
                          );
                        }}
                      </Form.Item>
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.Item>

          {/* 头像框背景图 */}
          <Form.Item noStyle dependencies={['productCategoryId']}>
            {({ getFieldValue }) => {
              const categoryId = getFieldValue('productCategoryId');
              return (
                <>
                  {(categoryId === 6 || categoryId === '6') && (
                    <Form.Item
                      name="backgroundImage"
                      label="头像框背景图"
                      preserve
                      extra="请上传商品头像框，支持：jpg、jpeg、png、webp"
                    >
                      <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
                        prevValues.backgroundImage !== currentValues.backgroundImage
                      }>
                        {() => {
                          const avatarValue = form.getFieldValue('backgroundImage');
                          // 如果字段值为空，尝试从编辑产品数据中获取
                          const finalAvatarValue = avatarValue || (editingProduct as any)?.backgroundImage || '';
                          
                          return (
                            <UploadImage 
                              value={finalAvatarValue}
                              onChange={(url) => form.setFieldValue('backgroundImage', url)}
                              fileTypes={[
                                'image/jpeg',
                                'image/jpg',
                                'image/png',
                                'image/webp'
                              ]}
                              // allowLottieSvga
                              width={104}
                              height={104}
                              showSizeHint={false}
                            />
                          );
                        }}
                      </Form.Item>
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.Item>


          {/* 使用 dependencies 确保依赖更新时重新渲染 */}
          <Form.Item noStyle dependencies={['productCategoryId']}>
            {({ getFieldValue }) => {
              const categoryId = getFieldValue('productCategoryId');
              const productImages = form.getFieldValue('productImages');
              return (
                <>
                  {(categoryId === 1 || categoryId === '1') && (
                    <Form.Item
                      name="productImages"
                      label="壁纸文件"
                      rules={[
                        {
                          required: true,
                          message: '请上传壁纸文件',
                          validator: (_, value) => {
                            console.log('壁纸文件验证值:', value);
                            if (!value || value.length === 0) {
                              return Promise.reject('请上传壁纸文件');
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      shouldUpdate
                    >
                      <ImagesUploader />
                    </Form.Item>
                  )}
                  {categoryId == 5 && (
                    <>
                      <Form.Item
                        name="productionRequiremenImage"
                        label="制作要求"
                        extra="【非定制可不填】实体分类的商品，需上传该商品对图片的要求，用户完成兑换后可见"
                      >
                        <ImagesUploader limit={1} />
                      </Form.Item>
                      <Form.Item name="productionRequiremenDesc">
                        <FormRichEditor
                          placeholder="最大5000字符"
                          maxLength={5000}
                        />
                      </Form.Item>
                      <Form.Item
                        label="订单有效期"
                        name="orderValidityPeriod"
                        rules={[{ required: true }]}
                      >
                        <TimeLimit />
                      </Form.Item>
                    </>
                  )}
                </>
              );
            }}
          </Form.Item>

          <Form.Item
            name="payType"
            label="支付类型"
            rules={[{ required: true, message: '请选择支付类型' }]}
          >
            <Select
              placeholder="请选择支付类型"
              disabled={commonOptions.payTypes.length === 0}
            >
              {commonOptions.payTypes.map((type) => (
                <Select.Option key={type.value} value={type.value}>
                  {type.text}
                </Select.Option>
              ))}
              {/* 确保所有支付类型都显示，即使后端没有返回 */}
              {Array.from(PayTypeMap.entries()).map(([value, text]) => {
                // 如果后端已经返回了该类型，跳过（避免重复）
                const exists = commonOptions.payTypes.some(
                  (type) => type.value === value
                );
                if (exists) return null;
                return (
                  <Select.Option key={value} value={value}>
                    {text}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="price"
            label="价格"
            rules={[{ required: true, message: '请输入价格' }]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%' }}
              placeholder="请输入价格"
            />
          </Form.Item>

          <Form.Item
            name="stock"
            label="库存"
            rules={[{ required: true, message: '请输入库存' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入库存"/>
          </Form.Item>

          <Form.Item name="stockCode" label="库存编码">
            <Input placeholder="请输入库存编码" />
          </Form.Item>

          <Form.Item
            name="num"
            label="一次性购买数量"
            rules={[{ required: true, message: '请输入一次性购买数量' }]}
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder="请输入一次性购买数量，如：AI使用次数10次"
            />
          </Form.Item>

          <Form.Item
            name="prePersonLimit"
            label="每人限购数量"
            rules={[{ required: true, message: '请输入每人限购数量' }]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder="请输入每人限购数量"
            />
          </Form.Item>

          <Form.Item
            name="shippingInformation"
            label="运输信息"
            rules={[{ required: true, message: '请输入运输信息' }]}
          >
            <Input.TextArea
              placeholder="请输入运输信息"
              maxLength={500}
              showCount
              rows={4}
            />
          </Form.Item>

          <Form.Item
            name="disclaimer"
            label="免责声明"
            rules={[{ required: true, message: '请输入免责声明' }]}
          >
            <Input.TextArea
              placeholder="请输入免责声明"
              maxLength={500}
              showCount
              rows={4}
            />
          </Form.Item>

          <Form.Item
            name="productDesc"
            label="商品描述"
            rules={[{ required: true, message: '请输入商品描述' }]}
          >
            <Input.TextArea
              placeholder="请输入商品描述"
              maxLength={500}
              showCount
              rows={4}
            />
          </Form.Item>
                                                    
          {/* 扩展内容 */}
          <Divider orientation="left">自定义内容</Divider>
          <Form.Item name="extContent" noStyle>
            <Form.List name="extContent">
              {(fields) =>
                fields.map((field, index) => (
                  <Form.Item key={field.key}>
                    <div style={{ border: '1px solid #ccc', padding: 10 }}>
                      {form.getFieldValue(['extContent', index, 'type']) ===
                        'image' && (
                        <Upload
                          listType="picture"
                          accept="image/*"
                          beforeUpload={() => false}
                          fileList={
                            form.getFieldValue([
                              'extContent',
                              index,
                              'imageInfo',
                            ])
                              ? [
                                  form.getFieldValue([
                                    'extContent',
                                    index,
                                    'imageInfo',
                                  ]),
                                ]
                              : []
                          }
                          onChange={(info) =>
                            handleExtraImageChange(info, index)
                          }
                          maxCount={1}
                        >
                          <Button icon={<UploadOutlined />}>上传图片</Button>
                        </Upload>
                      )}
                      {form.getFieldValue(['extContent', index, 'type']) ===
                        'text' && (
                        <div>
                          <RichEditor
                            ref={(editor) => {
                              if (editor) {
                                const editorRef = {
                                  current: editor,
                                };
                                editorRefsMap.current.set(index, editorRef);
                              }
                            }}
                            defaultValue={(() => {
                              const item = form.getFieldValue([
                                'extContent',
                                index,
                              ]);
                              if (item?.text) {
                                try {
                                  return JSON.parse(item.text);
                                } catch (error) {
                                  console.error('解析富文本内容失败:', error);
                                  return '';
                                }
                              }
                              return '';
                            })()}
                            maxLength={5000}
                          />
                          <div style={{ color: '#ccc', fontSize: 12 }}>
                            最大5000字符
                          </div>
                        </div>
                      )}
                      <Flex style={{ marginTop: 10 }} justify="end">
                        <Button
                          onClick={() => handleRemoveExtraContent(index)}
                          icon={<DeleteOutlined />}
                        />
                      </Flex>
                    </div>
                  </Form.Item>
                ))
              }
            </Form.List>
          </Form.Item>

          <Form.Item>
            <Space size="small">
              <Button onClick={() => handleAddExtraContent('image')}>
                新增图片字段
              </Button>
              <Button onClick={() => handleAddExtraContent('text')}>
                新增文本字段
              </Button>
            </Space>
          </Form.Item>
        </Spin>
      </Form>
    </Drawer>
  );
};

export default memo(ProductDrawer);
